import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { deleteNoteById, loadNotesByContract } from 'app/state/ducks/notes/thunks'
import { loadAllDeals } from 'app/state/ducks/deals/thunks'
import { getNotesForType } from 'app/state/ducks/notes/selectors'
import { getDeals } from 'app/state/ducks/deals/selectors'
import NotesFilterHeader from 'app/views/components/notes-and-attachment/note-filters-header'
import NotesBody from 'app/views/components/notes-and-attachment/notes-body'
import NotesTable from 'app/views/components/notes-and-attachment/notes-table'
import NotesForm from 'app/views/components/notes-and-attachment/notes-form'
import { Note } from 'app/models/note-params'
import { ContractType } from 'app/models/common-types'
import { SuccessNotification, ErrorNotification } from 'app/views/components/notifications/notification'
import { CustomLoader } from '../loader/loader'
import { loadAllFacilities } from 'app/state/ducks/facilities/thunks'
import { loadEntities } from 'app/state/ducks/entities/thunks'
import { getFacilities } from 'app/state/ducks/facilities/selectors'
import { getEntities } from 'app/state/ducks/entities/selectors'
import 'app/styles/components/notes.css'


export default function NotesPage() {
  const params = useParams()
  const dispatch = useDispatch()
  const [filteredNotes, setFilteredNotes] = useState<Note[]>([]); // Track filtered notes
  const deals = useSelector(getDeals)
  const facilities = useSelector(getFacilities)
  const entities = useSelector(getEntities)

  const [contractType, setContractType] = useState<ContractType | null>(null)
  const [contractId, setContractId] = useState<string | null>(null)
  const [selectedNote, setSelectedNote] = useState<Note | null>(null)
  const [isViewOnly, setIsViewOnly] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const loadData = async () => {
      if ('dealId' in params) {
        await dispatch(loadAllDeals())
      } else if ('facilityId' in params) {
        await dispatch(loadAllFacilities())
      } else if ('entityId' in params) {
        await dispatch(loadEntities())
      }
      setIsLoading(false) // Set loading to false when done
    }
    loadData()
  }, [params, dispatch])

  useEffect(() => {
    let newContractType: ContractType | null = null
    let newContractId: string | null = null

    if ('dealId' in params) {
      newContractType = ContractType.Deals
      newContractId = params.dealId as string
    } else if ('facilityId' in params) {
      newContractType = ContractType.Facilities
      newContractId = params.facilityId as string
    } else if ('entityId' in params) {
      newContractType = ContractType.Entities
      newContractId = params.entityId as string
    }

    setContractType(newContractType)
    setContractId(newContractId)

    if (newContractType && newContractId) {
      dispatch(loadNotesByContract(newContractId, newContractType))
    }
  }, [params, dispatch])

  const notes = useSelector((state: any) =>
    contractType ? getNotesForType(contractType)(state) : []
  )

  useEffect(() => {
    setFilteredNotes(notes ?? [])
  }, [notes])

  const handleDeleteNote = async (id: string) => {
    try {
      const result = await dispatch(deleteNoteById(id) as any)
      if (!result.success) {
        ErrorNotification({ message: result.payload, title: 'Delete Note Failed' })
        return
      }

      // Reload notes list after deletion
      if (contractId && contractType) {
        dispatch(loadNotesByContract(contractId, contractType))
      }

      // Clear the form if the deleted note was selected prior to deleting
      if (selectedNote?.id === id) {
        setSelectedNote(null)
        setIsViewOnly(false)
      }

      SuccessNotification({
        message: 'The note has been successfully deleted.',
        title: 'Delete Successful',
      })
    } catch (error) {
      ErrorNotification({ message: `An unexpected error occurred: ${error}`, title: 'Error' })
    }
  }

  const handleEditNote = (noteId: string) => {
    const noteToEdit = filteredNotes.find(note => note.id === noteId) || null
    setSelectedNote(noteToEdit)
    setIsViewOnly(false) // Enable editing
  }

  const handleViewNote = (noteId: string) => {
    const noteToView = filteredNotes.find(note => note.id === noteId) || null
    if (noteToView) {
      setSelectedNote(noteToView)
      setIsViewOnly(true) // Disable editing
    }
  }

  // Construct contract using contractId and contractType
  const contract = contractId && contractType ? {
    id: contractId,
    ...(contractType === ContractType.Deals
      ? deals.find(deal => deal.id === contractId) || { adminEntity: [] }
      : contractType === ContractType.Facilities
        ? facilities.find(facility => facility.id === contractId) || { adminEntity: [] }
        : contractType === ContractType.Entities
          ? entities.find(entity => entity.id === contractId) || { adminEntity: [] }
          : { adminEntity: [] }
    )
  } : { id: '', adminEntity: [] }

  // Only render NotesForm if contractType and contract are valid
  const renderNotesForm = contractType && contract

  return (
    <div className="notes-container">
      <NotesFilterHeader notes={filteredNotes ?? []} setSelectedNote={setSelectedNote} setIsViewOnly={setIsViewOnly} />
      <NotesBody>
        {isLoading && <div className='notes-loader-container'><CustomLoader /></div>}
        {!isLoading && (<>
          {renderNotesForm ? (
            <NotesForm contractType={contractType} contract={contract} selectedNote={selectedNote} setSelectedNote={setSelectedNote} isViewOnly={isViewOnly} setIsViewOnly={setIsViewOnly} />
          ) : null}
          <NotesTable notes={notes ?? []} onDeleteNote={handleDeleteNote} onEditNote={handleEditNote} onViewNote={handleViewNote} />
        </>
        )}
      </NotesBody>
    </div>
  )
}